import { prodApiLSKey } from '@/components/QATools/ls-keys';
import { createHttpAgent, getSessionApiService } from '@/utils/agents/session-api';
import { getSessionManager } from '@/utils/session';
import {
  API_SDK_URL,
  BASE_URL,
  COMMON_API_BASE_URL,
  PRODUCTION,
  VITE_ANALYTICS_PARSE_API_URL,
  VITE_BM_API_BASE_URL,
  VITE_PARSE_API_URL,
} from '@env';
import {
  CardComparisonApiService,
  FinanceApiService,
  NoticeApiService,
  PaymentCardApiService,
  ProjectApiService,
  ReferralApiService,
  SeoAnalyticsApiService,
  SeoApiService,
  SeoCatalogApiService,
  SeoUserApiService,
  UserApiService,
} from '@marpla/api-sdk';
import isObject from 'lodash/isObject';
import { useCallback, useMemo } from 'react';

type EndpointFunction<T = unknown> = (args?: any) => Promise<T>;
type LegacyEndpointMap = Omit<SeoApiFacade, 'token' | 'resetToken' | 'setToken' | 'isAuthorized'>;

interface SeoApiFacade {
  token: string;
  resetToken: () => Promise<void>;
  setToken: (data: { token: string; isSession: boolean }) => void;
  isAuthorized: boolean;
  getReportArticleData: EndpointFunction;
  getReportData: EndpointFunction;
  getReportDataFast: EndpointFunction;
  getReportCategories: EndpointFunction;
  getProjects: EndpointFunction;
  getProjectArticles: EndpointFunction;
  updateDailystats: EndpointFunction;
  updateDailystat: EndpointFunction;
  updateDailystatCategory: EndpointFunction;
  updateDailystatsCategory: EndpointFunction;
  updateDailystatsCarousel: EndpointFunction;
  getProductCarouselAnchorArticles: EndpointFunction;
  dispatchReportDataAction: EndpointFunction;
  createList: EndpointFunction;
  startParser: EndpointFunction;
  getParserState: EndpointFunction;
  activatePromocode: EndpointFunction;
  getFinanceInfo: EndpointFunction;
  getTransactionHistory: EndpointFunction;
  getReferralsIndex: EndpointFunction;
  createProject: EndpointFunction;
  createAutoProject: EndpointFunction;
  editProject: EndpointFunction;
  findAdditionalPhrasesBySubject: EndpointFunction;
  findAdditionalPhrasesBySubjectImproved: EndpointFunction;
  findAdditionalPhrasesByArticles: EndpointFunction;
  getDops: EndpointFunction;
  duplicateProject: EndpointFunction;
  deleteProject: EndpointFunction;
  getUserData: EndpointFunction;
  getUserApiKeys: EndpointFunction;
  addUserApiKey: EndpointFunction;
  editUserApiKey: EndpointFunction;
  deleteUserApiKey: EndpointFunction;
  activateBM: EndpointFunction;
  parsePhrase: EndpointFunction;
  editList: EndpointFunction;
  deleteList: EndpointFunction;
  addArticleProject: EndpointFunction;
  saveConfig: EndpointFunction;
  getConfig: EndpointFunction;
  getCardAnalyse: EndpointFunction;
  editCommonQueriesList: EndpointFunction;
  deleteCommonQueriesList: EndpointFunction;
  addCommonQueriesList: EndpointFunction;
  dispatchPhrasesLibraryAction: EndpointFunction;
  getCommonQueriesLists: EndpointFunction;
  getProjectInfo: EndpointFunction;
  getReferralsFromDate: EndpointFunction;
  getAccessSensitiveData: EndpointFunction;
  parsePriorityByPhrase: EndpointFunction;
  getAlsoFound: EndpointFunction;
  createPayment: EndpointFunction;
  createPaymentKassa: EndpointFunction;
  checkPayment: EndpointFunction;
  parseCategory: EndpointFunction;
  parseProjectCategories: EndpointFunction;
  getPromotionTable: EndpointFunction;
  getReportStatsHistory: EndpointFunction;
  getComparableCards: EndpointFunction;
  addCardsToCompare: EndpointFunction;
  getPhrasesToCompare: EndpointFunction;
  getCompareData: EndpointFunction;
  deleteComparableCard: EndpointFunction;
  getCompareDataWithBoosts: EndpointFunction;
  parsePhraseArticles: EndpointFunction;
  getNotifications: EndpointFunction;
  getPhrasesFrequencyByMonth: EndpointFunction;
  getMoscowDateTime: EndpointFunction;
  getUserCards: EndpointFunction;
  getPaymentCards: EndpointFunction;
  deletePaymentCards: EndpointFunction;
  postPaymentCards: EndpointFunction;
  getAbTests: EndpointFunction;
  getAbPackage: EndpointFunction;
  getAutopaySettings: EndpointFunction;
  putAutopaySettings: EndpointFunction;
  getTariffs: EndpointFunction;
}

function getDevBaseUrl(): string {
  if (typeof window === 'undefined') {
    return BASE_URL;
  }

  return window.localStorage.getItem(prodApiLSKey) === 'true'
    ? 'https://prod-api.marpla.ru/'
    : BASE_URL;
}

function isResponseObject(value: unknown): value is Record<string, unknown> {
  return isObject(value) && !Array.isArray(value);
}

async function withLegacyErrorObject<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    const typedError = error as {
      details?: unknown;
      response?: { data?: unknown; status?: number };
      status?: number;
      statusCode?: number;
    };
    const responseData = typedError.details ?? typedError.response?.data;

    if (isResponseObject(responseData)) {
      return {
        ...responseData,
        error: typedError.statusCode ?? typedError.response?.status ?? typedError.status,
      } as T;
    }

    throw error;
  }
}

function wrapLegacyEndpointErrors<T extends Record<string, EndpointFunction>>(endpoints: T): T {
  const wrappedEndpoints = {} as T;

  for (const key of Object.keys(endpoints) as (keyof T)[]) {
    wrappedEndpoints[key] = ((args?: unknown) =>
      withLegacyErrorObject(endpoints[key](args))) as T[keyof T];
  }

  return wrappedEndpoints;
}

function getLegacyApiBaseUrl(): string {
  return PRODUCTION ? BASE_URL : getDevBaseUrl();
}

function getSupportChatApiBaseUrl(): string {
  return (COMMON_API_BASE_URL || API_SDK_URL || '').replace(/\/common\/?$/, '');
}

let financeApiService: FinanceApiService | undefined;
let seoApiService: SeoApiService | undefined;
let projectApiService: ProjectApiService | undefined;
let seoUserApiService: SeoUserApiService | undefined;
let paymentCardApiService: PaymentCardApiService | undefined;
let referralApiService: ReferralApiService | undefined;
let cardComparisonApiService: CardComparisonApiService | undefined;
let seoAnalyticsApiService: SeoAnalyticsApiService | undefined;
let seoCatalogApiService: SeoCatalogApiService | undefined;
let supportChatUserApiService: UserApiService | undefined;
let noticeApiService: NoticeApiService | undefined;

function getFinanceApiService(): FinanceApiService {
  return (financeApiService ??= new FinanceApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

function getSeoApiService(): SeoApiService {
  return (seoApiService ??= new SeoApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

function getProjectApiService(): ProjectApiService {
  return (projectApiService ??= new ProjectApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

function getSeoUserApiService(): SeoUserApiService {
  return (seoUserApiService ??= new SeoUserApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

function getPaymentCardApiService(): PaymentCardApiService {
  return (paymentCardApiService ??= new PaymentCardApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

function getReferralApiService(): ReferralApiService {
  return (referralApiService ??= new ReferralApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

function getCardComparisonApiService(): CardComparisonApiService {
  return (cardComparisonApiService ??= new CardComparisonApiService(
    createHttpAgent(getLegacyApiBaseUrl()),
    getSessionManager(),
  ));
}

export function getSeoAnalyticsApiService(): SeoAnalyticsApiService {
  return (seoAnalyticsApiService ??= new SeoAnalyticsApiService(
    createHttpAgent(VITE_PARSE_API_URL),
    getSessionManager(),
  ));
}

export function getSeoCatalogApiService(): SeoCatalogApiService {
  return (seoCatalogApiService ??= new SeoCatalogApiService(
    createHttpAgent(VITE_ANALYTICS_PARSE_API_URL),
    getSessionManager(),
  ));
}

export function getSeoFinanceApiService(): FinanceApiService {
  return getFinanceApiService();
}

export function getSupportChatUserApiService(): UserApiService {
  return (supportChatUserApiService ??= new UserApiService(
    createHttpAgent(getSupportChatApiBaseUrl()),
    getSessionManager(),
  ));
}

export function getNoticeApiService(): NoticeApiService {
  return (noticeApiService ??= new NoticeApiService(
    createHttpAgent(VITE_BM_API_BASE_URL),
    getSessionManager(),
  ));
}

function createSeoApiFacade(): LegacyEndpointMap {
  const financeApi = getFinanceApiService();
  const seoApi = getSeoApiService();
  const projectApi = getProjectApiService();
  const seoUserApi = getSeoUserApiService();
  const paymentCardApi = getPaymentCardApiService();
  const referralApi = getReferralApiService();
  const cardComparisonApi = getCardComparisonApiService();
  const analyticsApi = getSeoAnalyticsApiService();
  const noticeApi = getNoticeApiService();

  const endpoints: LegacyEndpointMap = {
    getReportArticleData: ({ idProject, arts }) =>
      seoApi.getReportArticleData({ idProject }, { arts }),
    getReportData: ({ idProject, art }) => seoApi.getReportData({ idProject, art }),
    getReportDataFast: (query) => seoApi.getReportDataFast(query),
    getReportCategories: ({ idProject, art }) => seoApi.getReportCategories({ idProject, art }),
    getProjects: () => projectApi.getProjects(),
    getProjectArticles: ({ idProject }) => projectApi.getProjectArticles({ idProject }),
    updateDailystats: (body) => analyticsApi.updateProductPhraseDailystats(body),
    updateDailystat: (body) => analyticsApi.updateProductPhraseDailystat(body),
    updateDailystatCategory: (body) => analyticsApi.updateProductCatalogDailystat(body),
    updateDailystatsCategory: (body) => analyticsApi.updateProductCatalogDailystats(body),
    updateDailystatsCarousel: (body) => analyticsApi.updateProductCarouselDailystats(body),
    getProductCarouselAnchorArticles: (query) =>
      analyticsApi.getProductCarouselAnchorArticles(query),
    dispatchReportDataAction: ({ idProject, ...body }) =>
      seoApi.dispatchReportDataAction({ idProject }, body),
    createList: ({ idProject, name }) => seoApi.createList({ idProject }, { name }),
    startParser: ({ idProject, ...body }) => seoApi.startParser({ idProject }, body),
    getParserState: ({ idProject }) => seoApi.getParserState({ idProject }),
    activatePromocode: ({ promocode }) => financeApi.activatePromocode({ promocode }),
    getFinanceInfo: () => financeApi.getFinanceInfo(),
    getTransactionHistory: () => financeApi.getTransactionHistory(),
    getReferralsIndex: () => referralApi.getReferralsIndex(),
    createProject: (body) => projectApi.createProject(body),
    createAutoProject: ({ articleId }) => projectApi.createAutoProject({ articleId }),
    editProject: ({ id, ...body }) => projectApi.editProject({ id }, body),
    findAdditionalPhrasesBySubject: (query) => projectApi.findAdditionalPhrasesBySubject(query),
    findAdditionalPhrasesBySubjectImproved: (query) =>
      projectApi.findAdditionalPhrasesBySubjectImproved(query),
    findAdditionalPhrasesByArticles: (body) => projectApi.findAdditionalPhrasesByArticles(body),
    getDops: (query) => projectApi.getDops(query),
    duplicateProject: ({ id }) => projectApi.duplicateProject({ id }),
    deleteProject: ({ id }) => projectApi.deleteProject({ id }),
    getUserData: () => seoUserApi.getUserData(),
    getUserApiKeys: () => seoUserApi.getUserApiKeys(),
    addUserApiKey: ({ art, ...body }) => seoUserApi.addUserApiKey({ art }, body),
    editUserApiKey: ({ id, ...body }) => seoUserApi.editUserApiKey({ id }, body),
    deleteUserApiKey: ({ id }) => seoUserApi.deleteUserApiKey({ id }),
    activateBM: () => referralApi.activateBM(),
    parsePhrase: ({ pid, art, ...body }) => seoApi.parsePhrase({ pid, art }, body),
    editList: ({ pid, id, name }) => projectApi.editList({ pid, id }, { name }),
    deleteList: ({ pid, id }) => projectApi.deleteList({ pid, id }),
    addArticleProject: (body) => projectApi.addArticleProject(body),
    saveConfig: ({ items }) => seoUserApi.saveConfig({ items }),
    getConfig: ({ names }) => seoUserApi.getConfig({ names }),
    getCardAnalyse: (query) => projectApi.getCardAnalyse(query),
    editCommonQueriesList: ({ pid, id, name }) =>
      projectApi.editCommonQueriesList({ pid, id }, { name }),
    deleteCommonQueriesList: ({ pid, id }) => projectApi.deleteCommonQueriesList({ pid, id }),
    addCommonQueriesList: ({ idProject, name }) =>
      projectApi.addCommonQueriesList({ idProject }, { name }),
    dispatchPhrasesLibraryAction: ({ idProject, ...body }) =>
      projectApi.dispatchPhrasesLibraryAction({ idProject }, body),
    getCommonQueriesLists: ({ idProject }) => projectApi.getCommonQueriesLists({ idProject }),
    getProjectInfo: ({ idProject }) => projectApi.getProjectInfo({ idProject }),
    getReferralsFromDate: (query) => referralApi.getReferralsFromDate(query),
    getAccessSensitiveData: () => seoUserApi.getAccessSensitiveData(),
    parsePriorityByPhrase: (body) => seoApi.parsePriorityByPhrase(body),
    getAlsoFound: ({ idProject, art }) => seoApi.getAlsoFound({ idProject, art }),
    createPayment: (body) => financeApi.createPayment(body),
    createPaymentKassa: (body) => financeApi.createPaymentKassa(body),
    checkPayment: (query) => financeApi.checkPayment(query),
    parseCategory: ({ pid, art, ...body }) => seoApi.parseCategory({ pid, art }, body),
    parseProjectCategories: ({ idProject }) => seoApi.parseProjectCategories({ idProject }),
    getPromotionTable: (query) => seoApi.getPromotionTable(query),
    getReportStatsHistory: (query) => seoApi.getReportStatsHistory(query),
    getComparableCards: ({ idProject }) => cardComparisonApi.getComparableCards({ idProject }),
    addCardsToCompare: ({ idProject, ...body }) =>
      cardComparisonApi.addCardsToCompare({ idProject }, body),
    getPhrasesToCompare: ({ idProject, ...query }) =>
      cardComparisonApi.getPhrasesToCompare({ idProject }, query),
    getCompareData: ({ idProject, ...body }) =>
      cardComparisonApi.getCompareData({ idProject }, body),
    deleteComparableCard: ({ idProject, ...body }) =>
      cardComparisonApi.deleteComparableCard({ idProject }, body),
    getCompareDataWithBoosts: ({ idProject, ...body }) =>
      cardComparisonApi.getCompareDataWithBoosts({ idProject }, body),
    parsePhraseArticles: ({ pid, ...body }) => seoApi.parsePhraseArticles({ pid }, body),
    getNotifications: () => noticeApi.get(),
    getPhrasesFrequencyByMonth: (query) => seoApi.getPhrasesFrequencyByMonth(query),
    getMoscowDateTime: () => seoApi.getMoscowDateTime(),
    getUserCards: () => seoUserApi.getUserCards(),
    getPaymentCards: () => paymentCardApi.getPaymentCards(),
    deletePaymentCards: ({ id }) => paymentCardApi.deletePaymentCard({ id }),
    postPaymentCards: ({ id }) => paymentCardApi.selectPaymentCard({ id }),
    getAbTests: () => financeApi.getAbTests(),
    getAbPackage: () => financeApi.getAbPackage(),
    getAutopaySettings: () => financeApi.getAutopaySettings(),
    putAutopaySettings: (body) => financeApi.putAutopaySettings(body),
    getTariffs: () => financeApi.getTariffs(),
  };

  return wrapLegacyEndpointErrors(endpoints);
}

export function useSeoApi(): SeoApiFacade {
  const endpoints = useMemo(() => createSeoApiFacade(), []);

  const resetToken = useCallback(async () => {
    const token = getSessionManager().refreshToken;

    getSessionManager().clearAll();

    if (token) {
      await getSessionApiService().logout({ refreshToken: token });
    }
  }, []);

  const setToken = useCallback(({ token: tokenArg }: { token: string; isSession: boolean }) => {
    getSessionManager().applySessionAccess(tokenArg as any);
  }, []);

  return {
    ...endpoints,
    token: getSessionManager().getAccessToken(),
    resetToken,
    setToken,
    isAuthorized: getSessionManager().isAuthorized,
  };
}
